import React, { useEffect, useState } from "react";
import CommentList from "./CommentList";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "../../../../db/firebase";
import { useTranslation } from "react-i18next";
import CommentInputModal from "../../Components/CommentInputModal";
import { HiOutlineDocumentText, HiOutlinePlus } from "react-icons/hi2";

interface Comment {
  id: string;
  userName: string;
  comment: string;
  timestamp: string;
  attachmentUrl?: string;
  attachmentType?: string;
  attachmentName?: string;
  isAudio?: boolean;
}

interface CommentSectionProps {
  eventId: string;
  initialComments: Comment[];
}

const CommentSection: React.FC<CommentSectionProps> = ({
  eventId,
  initialComments,
}) => {
  const { t } = useTranslation();
  const [comments, setComments] = useState<Comment[]>(initialComments || []);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    setComments(initialComments || []);
  }, [initialComments]);

  const handleAddComment = async (newComment: Comment) => {
    const updatedComments = [newComment, ...comments];
    const eventDocRef = doc(db, "events", eventId);
    await updateDoc(eventDocRef, { comments: updatedComments });
    setComments(updatedComments);
    setIsModalOpen(false);
  };

  const handleDeleteComment = async (commentId: string) => {
    const updatedComments = comments.filter((comment) => comment.id !== commentId);
    setComments(updatedComments);
    await updateDoc(doc(db, "events", eventId), { comments: updatedComments });
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-4 sm:px-6 py-4 sm:py-5 border-b border-gray-100 dark:border-gray-700">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-[#5932EA]/10 dark:bg-[#5932EA]/20 text-[#5932EA]">
            <HiOutlineDocumentText className="w-5 h-5" strokeWidth={2} />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
              {t("notes")}
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {comments.length} {t("notes").toLowerCase()}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setIsModalOpen(true)}
          className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-medium bg-[#5932EA] text-white hover:bg-[#4a28c7] transition-colors"
        >
          <HiOutlinePlus className="w-4 h-4" />
          {t("addNotesButton")}
        </button>
      </div>
      <div className="p-4 sm:p-6">
        <CommentList comments={comments} onDeleteComment={handleDeleteComment} />
      </div>

      <CommentInputModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onAddComment={handleAddComment}
      />
    </div>
  );
};

export default CommentSection;
